import { useId, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/pixel";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const COUNTRIES = ["India","UAE","Dubai","Abu Dhabi","Sharjah","Qatar","Bahrain","Kuwait","Oman","Saudi Arabia","Singapore","Malaysia","United Kingdom","Canada","Australia","New Zealand","United States","Other"];
const BUDGETS = ["₹1 Cr – ₹1.5 Cr","₹1.5 Cr – ₹2.5 Cr","₹2.5 Cr – ₹4 Cr","₹4 Cr+"];
const CONFIGS = ["2 BHK","3 BHK","4 BHK","Penthouse"];

export default function LeadForm({ compact = false, source = "website", showNRIFields = false, ctaLabel = "Request a Callback", onSuccess }) {
  const consentId = useId();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", country: "India", budget: "", configuration: "",
    purpose: "", timeline: "", currency: "INR", message: "", consent: true, source,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      // Strip empty optional fields so Pydantic EmailStr / other validators don't reject empty strings
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/leads", payload);
      // Meta Pixel — track high-value Lead event on every successful form submission
      track("Lead", {
        source,
        content_name: source,
        configuration: form.configuration || undefined,
        budget: form.budget || undefined,
        country: form.country || undefined,
        currency: "INR",
      });
      // Google Ads — conversion event on successful lead form submission
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-16567701217/rzPHCIHHno4dEOGdjNw9",
        });
      }
      toast.success(data.message || "Thank you. Our team will contact you shortly.");
      setForm({ ...form, name: "", phone: "", email: "", budget: "", configuration: "", message: "" });
      onSuccess && onSuccess(data);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={`space-y-3 ${compact ? "" : ""}`} data-testid="lead-form">
      <Input data-testid="lead-name" placeholder="Full Name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-11" />
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="lead-phone" placeholder="Phone*" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-11" />
        <Input data-testid="lead-email" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] placeholder:text-[color:var(--pcv-text-2)]/60 h-11" />
      </div>
      {showNRIFields && (
        <div className="grid grid-cols-2 gap-3">
          <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
            <SelectTrigger data-testid="lead-country" className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] h-11"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger data-testid="lead-currency" className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] h-11"><SelectValue placeholder="Currency" /></SelectTrigger>
            <SelectContent>{["INR","AED","USD","SGD","GBP","EUR","CAD","AUD","QAR","BHD","OMR","KWD","SAR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Select value={form.configuration} onValueChange={(v) => setForm({ ...form, configuration: v })}>
          <SelectTrigger data-testid="lead-config" className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] h-11"><SelectValue placeholder="Configuration" /></SelectTrigger>
          <SelectContent>{CONFIGS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
          <SelectTrigger data-testid="lead-budget" className="bg-transparent border-[color:var(--pcv-border)] text-[color:var(--pcv-text)] h-11"><SelectValue placeholder="Budget" /></SelectTrigger>
          <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-start gap-2 pt-1">
        <Checkbox id={consentId} checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="lead-consent"
          className="mt-0.5 border-[color:var(--pcv-border)] data-[state=checked]:bg-[color:var(--pcv-gold)] data-[state=checked]:border-[color:var(--pcv-gold)]" />
        <label htmlFor={consentId} className="text-[11px] text-[color:var(--pcv-text-2)] leading-relaxed">
          I authorise Provident Winworth to contact me via phone / WhatsApp / email regarding this project.
        </label>
      </div>
      <button data-testid="lead-submit" type="submit" disabled={loading} className="btn-gold w-full mt-2 disabled:opacity-60 disabled:cursor-wait">
        {loading ? "Sending…" : ctaLabel}
      </button>
      <p className="text-[10px] text-center text-[color:var(--pcv-text-2)]/60">100% Privacy Guaranteed · No spam · We'll get back within business hours</p>
    </form>
  );
}
